import { useCallback, useEffect, useRef, useState } from 'react'
import {
  loadUpdateNoticeState,
  markSeen,
  pendingNotice,
  saveUpdateNoticeState,
  UPDATE_NOTICE_DELAY_MS,
  UPDATE_NOTICES,
  type UpdateNotice,
} from '../updateNotice'

export interface UpdateNoticeController {
  /** `null` whenever nothing is on screen. */
  notice: UpdateNotice | null
  /** Closing it is the same act however it is closed: the record is written before the modal goes. */
  dismiss: () => void
}

export interface UseUpdateNoticeOptions {
  /** Signed in and past the initial load. False keeps the notice off the login screen. */
  enabled: boolean
  /** Something else already owns the screen (a fixture sheet, a results reveal). A courtesy
   * notice waits rather than landing on top of what the user is doing. */
  isBlocked: boolean
}

/**
 * The runtime for the once-per-release notice. The rules it applies live in `src/updateNotice.ts`.
 *
 * Evaluated once, behind a short delay, so it arrives on a settled screen rather than during the
 * first paint. If the screen is busy when the delay elapses, the timer is simply not started —
 * the effect re-runs when `isBlocked` clears and the wait begins then.
 */
export function useUpdateNotice({
  enabled,
  isBlocked,
}: UseUpdateNoticeOptions): UpdateNoticeController {
  const [notice, setNotice] = useState<UpdateNotice | null>(null)
  /** One offer per session: dismissing must not re-arm the timer on the next render. */
  const settled = useRef(false)

  useEffect(() => {
    if (!enabled || isBlocked || settled.current) return
    const timer = setTimeout(() => {
      const pending = pendingNotice({ notices: UPDATE_NOTICES, state: loadUpdateNoticeState() })
      if (!pending) return
      settled.current = true
      setNotice(pending)
    }, UPDATE_NOTICE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [enabled, isBlocked])

  return {
    notice,
    // Not a state updater: writing to storage inside one would run twice under StrictMode's
    // double-invoke.
    dismiss: useCallback(() => {
      if (!notice) return
      const state = loadUpdateNoticeState()
      if (state) saveUpdateNoticeState(markSeen(state, UPDATE_NOTICES, notice.id))
      setNotice(null)
    }, [notice]),
  }
}
